import { Injectable, inject } from '@angular/core';
import { CmsDataService, CmsProductItem } from './cms-data.service';

export interface ModuleGroup {
  category: string;
  items: CmsProductItem[];
}

/**
 * Catálogo de productos/módulos SaaS de TodoSector.
 *
 * El contenido vive en el CMS bajo el `type_key` 'modulos' (un item por módulo,
 * con su precio en `location` y la capa en `category`). Añadir un módulo nuevo
 * = crear un item en el gestor de contenido, sin tocar este código.
 */
@Injectable({ providedIn: 'root' })
export class SaasProductsService {
  private cms = inject(CmsDataService);

  /** Módulos del catálogo (type_key='modulos'). */
  getModules(lang = 'es'): Promise<CmsProductItem[]> {
    return this.cms.getCmsItems('modulos', lang);
  }

  /**
   * Módulos agrupados por categoría (capa: Núcleo / Estándar / Vertical).
   * Respeta el orden definido en `categoryOrder`; las categorías no listadas
   * van al final por orden alfabético.
   */
  async getModulesByCategory(lang = 'es'): Promise<ModuleGroup[]> {
    const items = await this.getModules(lang);

    const groups = new Map<string, CmsProductItem[]>();
    for (const item of items) {
      const key = item.category || 'Otros';
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }

    const categoryOrder = ['Núcleo', 'Estándar', 'Vertical'];
    return Array.from(groups, ([category, groupItems]) => ({
      category,
      items: groupItems,
    })).sort((a, b) => {
      const ia = categoryOrder.indexOf(a.category);
      const ib = categoryOrder.indexOf(b.category);
      if (ia !== -1 || ib !== -1) {
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
      return a.category.localeCompare(b.category);
    });
  }
}
